import { useCallback } from 'react';
import apiClient from 'services/_infra/apiClient';
import { usePersonnel } from '../contexts/PersonnelContext';
import {
  extractPersonnelIsSupervisor,
  normalizeIdForApi,
  stringifyId,
} from '../features/PersonnelList/utils/personnelListUtils';

type PersonnelId = string | number;

/**
 * Shared responder personnel write operations.
 *
 * These own the request routes and payload shapes only: callers keep their own
 * error state, toasts, and refresh timing. Both the personnel roster and the
 * Microsoft security group duty sync go through here so the payload contract
 * lives in one place.
 *
 * Must be used within a `PersonnelProvider`.
 */
export const usePersonnelMutations = () => {
  const { personnel, fetchPersonnel } = usePersonnel();

  /**
   * Adds responder personnel records for the given Fusion user ids.
   * @param userIds Fusion user ids to add.
   * @returns {Promise<void>} Rejects when the request fails.
   */
  const createPersonnelForUserIds = useCallback(async (userIds: PersonnelId[]) => {
    const addPayload = (Array.isArray(userIds) ? userIds : []).map((userId) => ({
      userId,
      dutyPriority: [],
    }));
    if (!addPayload.length) return;
    await apiClient.post('/responder/personnel', addPayload);
  }, []);

  /**
   * Removes responder personnel records for the given Fusion user ids.
   * @param userIds Fusion user ids to remove.
   * @returns {Promise<void>} Rejects when the request fails.
   */
  const deletePersonnelForUserIds = useCallback(async (userIds: PersonnelId[]) => {
    const idsToDelete = Array.isArray(userIds) ? userIds : [];
    if (!idsToDelete.length) return;
    await apiClient.post('/responder/personnel/bulk-delete', { userIds: idsToDelete });
  }, []);

  /**
   * Replaces the duty assignment(s) for a single personnel record.
   * `isSupervisor` falls back to the value already on the record when omitted.
   * @param userId Fusion user id.
   * @param dutyIds Duty ids in priority order.
   * @param isSupervisor Supervisor flag, or null/undefined to keep the current value.
   * @returns {Promise<void>} Rejects when the request fails.
   */
  const setPersonnelDuties = useCallback(
    async (
      userId: PersonnelId,
      dutyIds: PersonnelId | PersonnelId[],
      isSupervisor?: boolean | null,
    ) => {
      if (userId == null || userId === '') {
        throw new Error('setPersonnelDuties requires a user id');
      }

      const currentPerson = personnel?.[stringifyId(userId)];
      const dutyPriority = (Array.isArray(dutyIds) ? dutyIds : [dutyIds])
        .filter((dutyId) => dutyId != null && dutyId !== '')
        .map((dutyId) => normalizeIdForApi(dutyId));
      const normalizedIsSupervisor =
        typeof isSupervisor === 'boolean'
          ? isSupervisor
          : extractPersonnelIsSupervisor(currentPerson);

      await apiClient.put(`/responder/personnel/${normalizeIdForApi(userId)}`, [
        {
          dutyPriority,
          isSupervisor: normalizedIsSupervisor,
        },
      ]);
    },
    [personnel],
  );

  return {
    createPersonnelForUserIds,
    deletePersonnelForUserIds,
    setPersonnelDuties,
    refreshPersonnel: fetchPersonnel,
  };
};

export default usePersonnelMutations;
